import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionHead } from "./SectionHead";
import { WhatsGlyph } from "./WhatsGlyph";
import { plans, waLink } from "@/data/site";

const PlanCard = ({ plan, index }) => {
  const cardBody = (
    <div
      className={`flex h-full flex-col p-7 ${
        plan.highlight ? "rounded-[calc(1.65rem-1.5px)] bg-surface" : "rounded-3xl border border-white/10 bg-surface"
      }`}
    >
      {plan.highlight ? (
        <span
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-wa px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#052e16] shadow-[0_0_30px_rgba(37,211,102,0.6)]"
          data-testid="plan-badge-best"
        >
          {plan.badge}
        </span>
      ) : plan.badge ? (
        <span className="mb-4 inline-flex w-fit rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
          {plan.badge}
        </span>
      ) : null}

      <p
        className={`font-mono text-[11px] uppercase tracking-[0.25em] ${
          plan.highlight ? "mt-3 text-neon" : "text-neon"
        }`}
      >
        {plan.name}
      </p>
      <h3 className="mt-2 font-display text-3xl font-bold uppercase tracking-tight text-white">
        {plan.duration}
      </h3>

      <div className="mt-6 flex items-end gap-1.5">
        <span
          className={`font-display text-5xl font-extrabold leading-none ${
            plan.highlight ? "text-white" : "text-white"
          }`}
          data-testid={`plan-price-${plan.id}`}
        >
          {plan.price}
        </span>
        <span className="pb-1 font-mono text-xs text-mist">{plan.billing}</span>
      </div>

      <p
        className={`mt-3 font-mono text-xs uppercase tracking-wider ${
          plan.highlight ? "font-bold text-wa" : "text-mist"
        }`}
        data-testid={`plan-equivalent-${plan.id}`}
      >
        {plan.highlight ? "★ " : ""}
        {plan.equivalent ?? "Cancele quando quiser"}
      </p>

      <div className="my-6 h-px bg-white/10" aria-hidden="true" />

      <ul className="flex-1 space-y-3">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm leading-snug text-mist">
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${plan.highlight ? "text-wa" : "text-neon"}`} />
            {f}
          </li>
        ))}
      </ul>

      <a
        href={waLink(plan.msg)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`plan-cta-${plan.id}`}
        className={`mt-8 inline-flex items-center justify-center gap-2.5 rounded-full px-6 py-3.5 font-display text-base font-bold uppercase tracking-wider transition-colors ${
          plan.highlight
            ? "bg-wa text-[#052e16] shadow-[0_12px_40px_-10px_rgba(37,211,102,0.55)] hover:bg-wa-hover"
            : "border border-wa/40 text-wa hover:bg-wa hover:text-[#052e16]"
        }`}
      >
        <WhatsGlyph className="h-4 w-4" />
        Assinar pelo WhatsApp
      </a>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className={
        plan.highlight
          ? "relative rounded-[1.65rem] bg-gradient-to-br from-neon via-vio to-wa p-[1.5px] shadow-[0_0_70px_-14px_rgba(0,240,255,0.5)] xl:-translate-y-4"
          : "relative transition-[border-color,box-shadow] hover:border-white/25"
      }
      data-testid={`plan-card-${plan.id}`}
    >
      {plan.highlight ? cardBody : <div className="h-full transition-colors">{cardBody}</div>}
    </motion.div>
  );
};

export const Pricing = () => (
  <section id="planos" className="relative py-24 sm:py-28" data-testid="pricing-section">
    <div className="pointer-events-none absolute left-1/2 top-1/3 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-vio/10 blur-[160px]" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead
        eyebrow="Planos e preços"
        title={
          <>
            Escolha seu plano e ative em minutos.
          </>
        }
        right={
          <p className="max-w-xs font-mono text-xs uppercase leading-relaxed tracking-wider text-mist">
            Todos com suporte VIP no WhatsApp e ativação imediata
          </p>
        }
      />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((plan, i) => (
          <PlanCard key={plan.id} plan={plan} index={i} />
        ))}
      </div>
    </div>
  </section>
);
